import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, HeartHandshake, Sprout } from "lucide-react";
import Reveal from "../components/Reveal";
import { images } from "../data/content";
import { useSiteContent } from "../lib/cms";
import { ArticleCard } from "./News";

const iconMap = { Users, HeartHandshake, Sprout };

export default function Home() {
  const { block, articles } = useSiteContent();
  const hero = block("home_hero");
  const pillars = block("home_pillars").items || [];
  const timeline = block("history_timeline").items || [];
  const latest = articles.slice(0, 3);
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative flex min-h-screen items-center">
        <img src={images.heroHall} alt="Freemasons' Hall, Coleman Street, Singapore" className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <div className="container-lodge relative z-10 pt-24">
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {hero.eyebrow}
          </motion.p>
          <motion.h1
            className="display-h1 mt-5 max-w-4xl text-parchment"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            {hero.title_lead}<span className="text-gold">{hero.title_highlight}</span>
            {hero.title_tail && <><br /> {hero.title_tail}</>}
          </motion.h1>
          <motion.p
            className="mt-6 max-w-xl font-sans text-lg leading-relaxed text-parchment/85"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
          >
            {hero.subtitle}
          </motion.p>
          <motion.div
            className="mt-9 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link to={hero.cta_primary_to || "/history"} className="btn-primary" data-testid="hero-learn-story">
              {hero.cta_primary_label} <ArrowRight size={16} />
            </Link>
            <Link to={hero.cta_secondary_to || "/visit"} className="btn-ghost" data-testid="hero-begin-inquiry">
              {hero.cta_secondary_label}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* TIMELINE TEASER */}
      <section className="bg-paper-texture bg-parchment py-24">
        <div className="container-lodge">
          <Reveal>
            <p className="eyebrow" style={{ color: "#046307" }}>180 Years, Three Centuries of Singapore</p>
            <h2 className="display-h2 mt-3 max-w-2xl text-sapphire-deep">
              A history older than the nation itself.
            </h2>
            <div className="gold-rule mt-6" />
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-4">
            {timeline.slice(0, 4).map((t, i) => (
              <Reveal key={`${t.year}-${i}`} delay={i * 0.1}>
                <div className="card-lift h-full rounded-2xl border border-sapphire-deep/10 bg-white p-7 shadow-sm">
                  <span className="font-trajan text-2xl font-semibold text-gold">{t.year}</span>
                  <h3 className="mt-3 font-display text-xl font-semibold text-sapphire-deep">{t.title}</h3>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-charcoal/70">
                    {(t.body || "").slice(0, 120)}…
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-12">
              <Link to="/history" className="link-underline text-base" data-testid="explore-timeline">
                Explore the full timeline <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT IS FREEMASONRY */}
      <section className="relative overflow-hidden bg-sapphire-deep py-24 text-parchment">
        <div className="container-lodge grid items-center gap-14 md:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-2xl shadow-heritage">
              <img src={images.squareCompass} alt="Antique brass square and compasses on parchment" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div>
              <p className="eyebrow">What is Freemasonry?</p>
              <h2 className="display-h2 mt-3 text-parchment">
                A peculiar system of morality, veiled in allegory.
              </h2>
              <p className="mt-6 font-sans text-lg leading-relaxed text-parchment/80">
                Freemasonry is one of the world's oldest fraternal organisations — a society of men
                concerned with moral and spiritual values, personal integrity and service. Using the
                tools of the stonemason as allegory, it helps good men become better.
              </p>
              <Link to="/freemasonry" className="btn-ghost mt-8" data-testid="home-learn-freemasonry">
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LIVING BROTHERHOOD */}
      <section className="bg-parchment py-24">
        <div className="container-lodge">
          <Reveal>
            <div className="text-center">
              <p className="eyebrow" style={{ color: "#046307" }}>A Living Brotherhood</p>
              <h2 className="display-h2 mt-3 text-sapphire-deep">More than history.</h2>
              <div className="gold-rule mx-auto mt-6" />
            </div>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {pillars.map((p, i) => {
              const Icon = iconMap[p.icon] || Users;
              return (
                <Reveal key={`${p.title}-${i}`} delay={i * 0.12}>
                  <div className="card-lift h-full rounded-2xl border border-sapphire-deep/10 bg-white p-8 text-center shadow-sm">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sapphire-deep/5 text-emerald">
                      <Icon size={28} strokeWidth={1.6} />
                    </div>
                    <h3 className="mt-6 font-display text-2xl font-semibold text-sapphire-deep">{p.title}</h3>
                    <p className="mt-3 font-sans text-[15px] leading-relaxed text-charcoal/70">{p.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* LATEST NEWS */}
      {latest.length > 0 && (
        <section className="bg-paper-texture bg-ivory py-24" data-testid="home-news-strip">
          <div className="container-lodge">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-6">
                <div>
                  <p className="eyebrow" style={{ color: "#046307" }}>From the Lodge</p>
                  <h2 className="display-h2 mt-3 text-sapphire-deep">Latest news.</h2>
                  <div className="gold-rule mt-6" />
                </div>
                <Link to="/news" className="link-underline text-base" data-testid="home-all-news">
                  All news & articles <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
            <div className="mt-14 grid gap-8 md:grid-cols-3">
              {latest.map((a, i) => <ArticleCard key={a.id} a={a} i={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* CONSIDERING MEMBERSHIP CTA */}
      <section className="relative overflow-hidden bg-emerald-deep py-24 text-parchment">
        <div className="bg-paper-texture absolute inset-0 opacity-40" />
        <div className="container-lodge relative text-center">
          <Reveal>
            <img src={images.crestSeal} alt="Lodge coat of arms" className="mx-auto h-24 w-24" />
            <h2 className="display-h2 mx-auto mt-6 max-w-2xl text-parchment">Considering membership?</h2>
            <p className="mx-auto mt-5 max-w-xl font-sans text-lg leading-relaxed text-parchment/80">
              Freemasonry does not solicit its members. If you are curious, the first step is always
              yours — and we would be glad to hear from you.
            </p>
            <Link to="/visit" className="btn-primary mt-9" data-testid="home-start-inquiry">
              Start an Inquiry <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
